import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, ArrowUpRight, Bell } from 'lucide-react';
import { useInView } from '@/hooks/useInView';
import { products } from '@/data/products';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function RevealSection({ children, className = '', delay = 0 }) {
  const [ref, isInView] = useInView();
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${className}`}
      style={{
        opacity: isInView ? 1 : 0,
        transform: isInView ? 'translateY(0)' : 'translateY(24px)',
        transitionDelay: `${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

function NotifyForm({ productSlug }) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus('loading');
    try {
      await axios.post(`${BACKEND_URL}/api/notify`, { email, product_slug: productSlug });
      setStatus('success');
      setEmail('');
    } catch {
      setStatus('error');
    }
  };

  return (
    <div data-testid="notify-form-container">
      <p className="font-inter font-medium text-sm text-km-gray-5 mb-3">Get notified when this launches:</p>
      <form onSubmit={handleSubmit} data-testid="notify-form" className="flex gap-2">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
          data-testid="notify-email-input"
          required
          className="flex-1 min-w-0 h-10 px-3 rounded-[10px] bg-km-gray-2 border border-km-gray-3 font-inter font-medium text-sm text-km-white placeholder:text-km-gray-5/70 focus:outline-none focus:ring-2 focus:ring-km-gold/60 focus:border-km-gold/40 transition-all duration-300"
        />
        <button
          type="submit"
          data-testid="notify-submit"
          disabled={status === 'loading'}
          className="inline-flex items-center justify-center h-10 px-4 rounded-[10px] bg-km-gold text-km-gray-1 hover:bg-km-gold-deep transition-colors duration-300 cursor-pointer disabled:opacity-50"
        >
          <Bell className="w-3.5 h-3.5" />
        </button>
      </form>
      {status === 'success' && (
        <p data-testid="notify-success" className="mt-2 font-inter font-medium text-xs text-km-gold">You're on the list. We'll let you know.</p>
      )}
      {status === 'error' && (
        <p data-testid="notify-error" className="mt-2 font-inter font-medium text-xs text-red-400">Something went wrong. Try again.</p>
      )}
    </div>
  );
}

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const product = products.find((p) => p.slug === slug);

  if (!product) {
    return (
      <div data-testid="product-not-found" className="px-6 lg:px-8 py-32 text-center">
        <h1 className="font-inter font-bold text-[20px] md:text-[24px] text-km-white">Product not found</h1>
        <Link to="/products" className="mt-4 inline-block font-inter font-medium text-sm text-km-gold hover:text-km-gold-deep transition-colors cursor-pointer">
          Back to products
        </Link>
      </div>
    );
  }

  const otherProducts = products.filter((p) => p.id !== product.id && !p.comingSoon);

  return (
    <div data-testid="product-detail-page" className="px-6 lg:px-8 pt-[60px] pb-[100px] md:pt-[80px] md:pb-[120px]">
      <div className="max-w-5xl mx-auto">
        <RevealSection>
          <button
            onClick={() => navigate(-1)}
            data-testid="back-button"
            className="font-inter font-medium text-sm text-km-gray-5 hover:text-km-white inline-flex items-center gap-2 cursor-pointer transition-colors duration-300 mb-12"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        </RevealSection>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
          <div className="lg:col-span-3">
            <RevealSection>
              <div className="flex items-center gap-3 mb-4">
                <span className="font-inter text-xs font-semibold tracking-[0.25em] uppercase text-km-gold">
                  {product.categoryLabel}
                </span>
                {product.comingSoon && (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full border border-km-gray-4/50 bg-km-gray-4/20 font-inter text-[10px] font-semibold tracking-[0.15em] uppercase text-km-gray-5">
                    Coming Soon
                  </span>
                )}
              </div>
              <h1 className="font-inter font-bold text-[38px] md:text-[54px] leading-[1.1] tracking-tight text-km-white">
                {product.name}
              </h1>
              {product.subtitle && (
                <p className="mt-2 font-inter font-medium text-lg text-km-gray-5">{product.subtitle}</p>
              )}
              <p className="mt-6 font-inter font-medium text-base md:text-lg text-km-gray-5 leading-[1.3] whitespace-pre-line">
                {product.longDescription}
              </p>
            </RevealSection>

            <RevealSection delay={100}>
              <div className="mt-12 p-[30px] rounded-[10px] bg-km-card-gradient border border-km-gray-3">
                <h2 className="font-inter font-medium text-[20px] md:text-[24px] leading-[1.2] text-km-white mb-6">
                  {product.comingSoon ? "What will be included" : "What's included"}
                </h2>
                <ul className="space-y-4">
                  {product.includes.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-km-gold mt-0.5 flex-shrink-0" />
                      <span className="font-inter font-medium text-base text-km-white/80">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </RevealSection>

            <RevealSection delay={200}>
              <div className="mt-[30px] p-[30px] rounded-[10px] bg-km-card-gradient border border-km-gray-3">
                <h2 className="font-inter font-medium text-[20px] md:text-[24px] leading-[1.2] text-km-white mb-4">Who it's for</h2>
                <p className="font-inter font-medium text-base text-km-gray-5 leading-[1.3]">{product.whoItsFor}</p>
                {product.notFor && (
                  <p className="mt-3 font-inter font-medium text-sm text-km-gray-5">
                    <strong className="font-semibold text-km-white">Not for:</strong> {product.notFor}
                  </p>
                )}
              </div>
            </RevealSection>
          </div>

          <div className="lg:col-span-2">
            <RevealSection delay={150}>
              <div
                className={`sticky top-28 p-8 rounded-[10px] bg-km-card-gradient ${
                  product.comingSoon ? 'border border-km-gray-3' : 'border-2 border-km-gold/30'
                }`}
              >
                <span className="font-inter text-xs font-semibold tracking-[0.2em] uppercase text-km-gold">
                  {product.categoryLabel}
                </span>

                {product.comingSoon ? (
                  <>
                    <p className="mt-4 font-inter font-bold text-[24px] text-km-gray-5">Coming Soon</p>
                    <p className="mt-2 font-inter font-medium text-sm text-km-gray-5">{product.delivery}</p>
                    <div className="mt-6">
                      <NotifyForm productSlug={product.slug} />
                    </div>
                  </>
                ) : (
                  <>
                    <p className="mt-4 font-inter font-bold text-[28px] md:text-[38px] text-km-white">
                      ${product.price} <span className="text-base font-medium text-km-gray-5">CAD</span>
                    </p>
                    <p className="mt-1 font-inter font-medium text-xs text-km-gray-5">Price shown in CAD. Your bank may apply currency conversion if you're outside Canada.</p>
                    <p className="mt-2 font-inter font-medium text-sm text-km-gray-5">{product.delivery}</p>
                    <a
                      href={product.checkoutUrl}
                      data-testid="buy-now-button"
                      className="mt-8 w-full inline-flex items-center justify-center gap-1 px-8 py-3.5 rounded-[10px] bg-km-gold text-km-gray-1 font-inter font-medium text-sm tracking-wide hover:bg-km-gold-deep transition-colors duration-300 cursor-pointer"
                    >
                      Buy Now <ArrowUpRight className="w-4 h-4 ml-1" />
                    </a>
                    <div className="mt-6 pt-6 border-t border-km-gray-3 space-y-3">
                      <p className="font-inter font-medium text-xs text-km-gray-5 flex items-center gap-2">
                        <Check className="w-3.5 h-3.5 text-km-gold" /> Instant Notion template access
                      </p>
                      <p className="font-inter font-medium text-xs text-km-gray-5 flex items-center gap-2">
                        <Check className="w-3.5 h-3.5 text-km-gold" /> Instant digital access via Notion
                      </p>
                      <p className="font-inter font-medium text-xs text-km-gray-5 flex items-center gap-2">
                        <Check className="w-3.5 h-3.5 text-km-gold" /> 5-part structured system
                      </p>
                    </div>
                    <p className="mt-4 font-inter font-medium text-xs text-km-gray-5 text-center">
                      Instant digital access.{' '}
                      <Link to="/refund-policy" className="text-km-gold hover:text-km-gold-deep transition-colors cursor-pointer">
                        Review refund policy
                      </Link>{' '}
                      before purchasing.
                    </p>
                  </>
                )}
              </div>
            </RevealSection>
          </div>
        </div>

        {otherProducts.length > 0 && !product.comingSoon && (
          <RevealSection delay={300}>
            <div className="mt-24 pt-16 border-t border-km-gray-3">
              <h2 className="font-inter font-medium text-[20px] md:text-[24px] leading-[1.2] text-km-white mb-8">Other products</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-[30px]">
                {otherProducts.map((p) => (
                  <Link
                    key={p.id}
                    to={`/products/${p.slug}`}
                    data-testid={`related-product-${p.slug}`}
                    className="group block p-[30px] rounded-[10px] bg-km-card-gradient border-2 border-km-gold/30 hover:border-km-gold/50 transition-colors duration-300 cursor-pointer"
                  >
                    <span className="font-inter text-xs font-semibold tracking-[0.2em] uppercase text-km-gold">
                      {p.categoryLabel}
                    </span>
                    <h3 className="mt-3 font-inter font-medium text-[20px] leading-[1.2] text-km-white group-hover:text-km-gold transition-colors duration-300">
                      {p.name}
                    </h3>
                    <p className="mt-2 font-inter font-medium text-sm text-km-gray-5 leading-[1.3]">{p.tagline}</p>
                    <p className="mt-4 font-inter font-bold text-lg text-km-white">
                      ${p.price} <span className="text-sm font-medium text-km-gray-5">CAD</span>
                    </p>
                  </Link>
                ))}
              </div>
            </div>
          </RevealSection>
        )}

        {product.comingSoon && (
          <RevealSection delay={300}>
            <div className="mt-24 pt-16 border-t border-km-gray-3 text-center">
              <h2 className="font-inter font-medium text-[20px] md:text-[24px] leading-[1.2] text-km-white mb-4">Available now</h2>
              <p className="font-inter font-medium text-base text-km-gray-5 leading-[1.3] mb-8">Check out the toolkit that's ready to use today.</p>
              <Link
                to="/products/high-stakes-narrative-pack"
                data-testid="available-now-cta"
                className="inline-flex items-center justify-center px-8 py-3.5 rounded-[10px] bg-km-gold text-km-gray-1 font-inter font-medium text-sm tracking-wide hover:bg-km-gold-deep transition-colors duration-300 cursor-pointer"
              >
                High-Stakes Narrative Pack: $249 CAD
              </Link>
            </div>
          </RevealSection>
        )}
      </div>
    </div>
  );
}
